import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import cancelScheduledSystemAction from '../api/cancelScheduledSystemAction';
import { formatScheduledDateBerlin } from '../utils/format';

interface Props {
  open: boolean;
  logId: number | null;
  providerName: string | null;
  scheduledAt: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function CancelScheduledActionDialog({ open, logId, providerName, scheduledAt, onClose, onSuccess }: Props) {
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [cancelMutation, { loading }] = useMutation(cancelScheduledSystemAction);

  useEffect(() => {
    if (open) {
      setReason('');
      setSubmitError(null);
      setTouched(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const reasonError = touched && !trimmed ? 'Bitte einen Grund angeben.' : null;

  const handleSubmit = async () => {
    setTouched(true);
    if (!trimmed || logId == null) return;

    try {
      const { data } = await cancelMutation({
        variables: { id: logId, reason: trimmed },
      });
      if (data?.cancelScheduledSystemAction?.success) {
        onSuccess();
      } else {
        setSubmitError(data?.cancelScheduledSystemAction?.message || 'Aktion konnte nicht abgebrochen werden.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSubmitError(message);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Geplanten Versand abbrechen</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Dienstleister</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{providerName || '—'}</Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Geplant für</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{formatScheduledDateBerlin(scheduledAt)}</Typography>
        </Box>

        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Grund für den Abbruch"
          placeholder="z. B. Berater hat sich gegen den Zugang entschieden"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (!touched) setTouched(true);
            if (submitError) setSubmitError(null);
          }}
          onBlur={() => setTouched(true)}
          error={!!reasonError}
          helperText={reasonError || 'Pflichtfeld — wird im Verlauf gespeichert.'}
          disabled={loading}
          inputProps={{ 'aria-label': 'Grund für den Abbruch' }}
        />

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
          Doch nicht
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={loading || !trimmed}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Abbrechen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CancelScheduledActionDialog;
